import type { ExpressionSpecification } from 'maplibre-gl';

import type { AttributeRule, AttributeRuleCondition } from '../rules/model';

function property(field: string): ExpressionSpecification {
  return ['get', field];
}

function propertyType(field: string, type: 'boolean' | 'number' | 'string') {
  return ['==', ['typeof', property(field)], type] as ExpressionSpecification;
}

function numericComparisonValue(condition: AttributeRuleCondition): number {
  if (typeof condition.value !== 'number' || !Number.isFinite(condition.value)) {
    throw new Error('A numeric attribute rule requires a finite comparison value.');
  }
  return condition.value;
}

function conditionExpression(condition: AttributeRuleCondition): ExpressionSpecification {
  const value = property(condition.field);
  switch (condition.operator) {
    case 'is-empty':
      return [
        'any',
        ['==', ['typeof', value], 'null'],
        ['all', propertyType(condition.field, 'string'), ['==', value, '']],
      ];
    case 'is-not-empty':
      return [
        'match',
        ['typeof', value],
        ['boolean', 'number'],
        true,
        'string',
        ['!=', value, ''],
        false,
      ];
    case 'is-true':
      return ['all', propertyType(condition.field, 'boolean'), ['==', value, true]];
    case 'is-false':
      return ['all', propertyType(condition.field, 'boolean'), ['==', value, false]];
    case 'equals':
      return [
        'all',
        propertyType(condition.field, typeof condition.value === 'number' ? 'number' : 'string'),
        ['==', value, condition.value],
      ];
    case 'does-not-equal':
      return [
        'all',
        propertyType(condition.field, typeof condition.value === 'number' ? 'number' : 'string'),
        ['!=', value, condition.value],
      ];
    case 'contains':
      return ['all', propertyType(condition.field, 'string'), ['in', condition.value, value]];
    case 'does-not-contain':
      return [
        'all',
        propertyType(condition.field, 'string'),
        ['!', ['in', condition.value, value]],
      ];
    case 'greater-than':
      return [
        'all',
        propertyType(condition.field, 'number'),
        ['>', value, numericComparisonValue(condition)],
      ];
    case 'greater-than-or-equal':
      return [
        'all',
        propertyType(condition.field, 'number'),
        ['>=', value, numericComparisonValue(condition)],
      ];
    case 'less-than':
      return [
        'all',
        propertyType(condition.field, 'number'),
        ['<', value, numericComparisonValue(condition)],
      ];
    case 'less-than-or-equal':
      return [
        'all',
        propertyType(condition.field, 'number'),
        ['<=', value, numericComparisonValue(condition)],
      ];
  }
}

/** Translate a validated Placeio Maps rule at the MapLibre rendering boundary. */
export function mapLibreAttributeRuleExpression(rule: AttributeRule): ExpressionSpecification {
  if (rule.kind === 'condition') return conditionExpression(rule);
  return [
    rule.operator === 'and' ? 'all' : 'any',
    ...rule.children.map(mapLibreAttributeRuleExpression),
  ];
}
